import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Printer, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import * as packagesService from "../services/packagesService";

export default function PrintSpeedafLabels({ filter, variant = "default", size = "sm", className = "" }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const printLabelsMutation = useMutation({
    mutationFn: async () => {
      // Remove pagination parameters before sending to print endpoint
      const { limit, page, offset, ...cleanFilters } = filter;
      return packagesService.printBulkSpeedafLabels(cleanFilters);
    },
    onSuccess: (data) => {
      // Create a blob URL and trigger download
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `speedaf_labels_${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t("speedaf.labelsDownloaded"));
      setIsOpen(false);
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message ||
        error.message ||
        t("speedaf.labelsPrintFailed");
      toast.error(message);
    },
  });

  const handlePrintLabels = () => {
    printLabelsMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <Printer className="h-4 w-4 mr-2" />
          {t("speedaf.printLabels")}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("speedaf.printSpeedafLabels")}</DialogTitle>
          <DialogDescription>
            {t("speedaf.printLabelsDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t("speedaf.printLabelsWarning")}
            </AlertDescription>
          </Alert>

          <div className="text-sm text-muted-foreground">
            <p>
              {t("speedaf.currentFilterInfo")}
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              {filter.status && (
                <li>
                  {t("common.status")}: <strong>{filter.status}</strong>
                </li>
              )}
              {filter.city && (
                <li>
                  {t("common.city")}: <strong>{filter.city}</strong>
                </li>
              )}
              {filter.user && (
                <li>
                  {t("common.client")}: <strong>{filter.user}</strong>
                </li>
              )}
              {filter.livreur && (
                <li>
                  {t("common.livreur")}: <strong>{filter.livreur}</strong>
                </li>
              )}
              {filter.trackingNumber && (
                <li>
                  {t("common.trackingNumber")}: <strong>{filter.trackingNumber}</strong>
                </li>
              )}
              {filter.customerPhone && (
                <li>
                  {t("common.phone")}: <strong>{filter.customerPhone}</strong>
                </li>
              )}
              {filter.customerName && (
                <li>
                  {t("common.customer")}: <strong>{filter.customerName}</strong>
                </li>
              )}
              {filter.startDate && (
                <li>
                  {t("common.startDate")}: <strong>{new Date(filter.startDate).toLocaleDateString()}</strong>
                </li>
              )}
              {filter.endDate && (
                <li>
                  {t("common.endDate")}: <strong>{new Date(filter.endDate).toLocaleDateString()}</strong>
                </li>
              )}
              {!filter.status &&
                !filter.city &&
                !filter.user &&
                !filter.livreur &&
                !filter.trackingNumber &&
                !filter.customerPhone &&
                !filter.customerName &&
                !filter.startDate &&
                !filter.endDate && (
                  <li>{t("speedaf.noFiltersApplied")}</li>
                )}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setIsOpen(false)}
            disabled={printLabelsMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handlePrintLabels}
            disabled={printLabelsMutation.isPending}
          >
            {printLabelsMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("speedaf.printing")}
              </>
            ) : (
              <>
                <Printer className="h-4 w-4 mr-2" />
                {t("speedaf.printLabels")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
